import { Dispatch } from "react";
import axios, { AxiosResponse } from "axios";
import { AnyIfEmpty } from "react-redux";
import { CLEAR_EXPORT_STAFF_STATE, GET_EXPORT_STAFF_FAILURE, GET_EXPORT_STAFF_REQUEST, GET_EXPORT_STAFF_SUCCESS, GET_STAFF_LIST_FAILURE, GET_STAFF_LIST_REQUEST, GET_STAFF_LIST_SUCCESS } from "../constants/staff.constants";
import { FailedImportData, StaffFormProps, StaffUpdate } from "../../../components/staffManagement/StaffManagement";

export interface Location {
    locationId: string | null;
    locationName: string | null;
}

interface InvitedBy {
    id: string;
    name: string;
    email: string;
    role: string | null;
    locations: Location[];
}

export interface UserResponse {
    id: string;
    name: string;
    email: string;
    role: string;
    invitedBy: InvitedBy[];
    roleId: string;
}


const getStaffListRequest = () => {
    return {
        type: GET_STAFF_LIST_REQUEST
    }
}

const getStaffListSuccess = (data: AnyIfEmpty<object>) => {
    return {
        type: GET_STAFF_LIST_SUCCESS,
        payload: data
    }
}

const getStaffListFailure = (errMsg: string) => {
    return {
        type: GET_STAFF_LIST_FAILURE,
        payload: errMsg
    }
}

const getExportStaffRequest = () => {
    return {
        type: GET_EXPORT_STAFF_REQUEST
    }
}

const getExportStaffSuccess = (data: AnyIfEmpty<object>) => {
    return {
        type: GET_EXPORT_STAFF_SUCCESS,
        payload: data
    }
}

const getExportStaffFailure = (errMsg: string) => {
    return {
        type: GET_EXPORT_STAFF_FAILURE,
        payload: errMsg
    }
}       

export const addStaffMember = (staffData: StaffFormProps, onSuccess: (message: string) => void, onFailure: (message: string) => void) => {
    return () => {
        return axios.post(`/admin/staff/invite`, staffData)
            .then((response: AxiosResponse) => {
                if (response?.data?.statusCode === 200) {
                    onSuccess(response?.data?.message)
                } else {
                    onFailure(response?.data?.message)
                }
            })
            .catch((error) => {
                onSuccess(error?.response?.data?.message)
            });
    };
};

export const getStaffList = (searchText: string | null, roles: string[], properties: string[], page: number = 1, limit: number = 20, shouldAppend: boolean = false) => {
    return (dispatch: Dispatch<AnyIfEmpty<object>>) => {
        dispatch(getStaffListRequest());
        return axios.get(`/admin/staff/invited-Staff-Locations`, {
            params: {
                locationFilter: properties?.length ? JSON.stringify(properties) : JSON.stringify([""]),
                roleFilter: roles?.length ? JSON.stringify(roles) : JSON.stringify([""]),
                searchTerm: encodeURIComponent(searchText || ""),
                page: page,
                limit: limit
            }
        }).then((response: AxiosResponse) => {
            if (response?.data?.statusCode === 200) {
                dispatch(getStaffListSuccess({
                    staffLocations: response?.data?.staffLocations,
                    count: response?.data?.pagination?.total,
                    shouldAppend: shouldAppend
                }))
            } else {
                dispatch(getStaffListFailure(response?.data?.messag))
            }
        }).catch((error) => {
            dispatch(getStaffListFailure(error?.response?.data?.messag))
        });
    };
};

export const getStaffDetail = (id: string, onSuccess: (data: UserResponse) => void, onFailure: () => void) => {
    return () => {
        return axios.get(`/admin/staff/get-one-staff/${id}`)
            .then((response: AxiosResponse) => {
                if (response?.data?.statusCode === 200) {
                    onSuccess(response?.data?.staffLocations)
                } else {
                    onFailure()
                }
            }).catch(() => {
                onFailure()
            });
    };
};

export const updateStaff = (staff: StaffUpdate, onSuccess: (message: string) => void, onFailure: (message: string) => void) => {
    return () => {
        return axios.patch(`/admin/staff/edit-staff`, staff)
            .then((response: AxiosResponse) => {
                if (response?.data?.statusCode === 200) {
                    onSuccess(response?.data?.message)
                } else {
                    onFailure(response?.data?.message)
                }
            }).catch((error) => {
                onFailure(error?.response?.data?.message)
            });
    };
};

export const deactivateStaff = (id: string, onSuccess: (message: string) => void, onFailure: (message: string) => void) => {
    return () => {
        return axios.patch(`/admin/admin-deactive/${id}`)
            .then((response: AxiosResponse) => {
                if (response?.data?.statusCode === 200) {
                    onSuccess(response?.data?.message)
                } else {
                    onFailure(response?.data?.message)
                }
            }).catch((error) => {
                onFailure(error?.response?.data?.message)
            });
    };
};

export const activateStaff = (id: string, onSuccess: (message: string) => void, onFailure: (message: string) => void) => {
    return () => {
        return axios.patch(`/admin/admin-active/${id}`)
            .then((response: AxiosResponse) => {
                if (response?.data?.statusCode === 200) {
                    onSuccess(response?.data?.message)
                } else {
                    onFailure(response?.data?.message)
                }
            }).catch((error) => {
                onFailure(error?.response?.data?.message)
            });
    };
};

export const getStaffDetailyEmail = (email: string, onSuccess: (data: UserResponse) => void, onFailure: () => void) => {
    return () => {
        return axios.get(`/admin/staff-me`, {
            headers: { email: email }
        }).then((response: AxiosResponse) => {
            if (response?.data?.statusCode === 200) {
                onSuccess(response?.data?.staff)
            } else {
                onFailure()
            }
        }).catch(() => {
            onFailure()
        });
    };
};

export const getExportStaff = () => {
    return (dispatch: Dispatch<AnyIfEmpty<object>>) => {
        dispatch(getExportStaffRequest());

        axios
            .get("/admin/staff/export-staff")
            .then((response) => {
                if (response.status === 200 && response.data?.url) {
                    const fileUrl = response.data.url;

                    // Open CSV in new tab
                    window.open(fileUrl, "_self");

                    dispatch(getExportStaffSuccess(response.data.message));
                } else {
                    dispatch(
                        getExportStaffFailure(
                            response.data?.message || "No file URL returned"
                        )
                    );
                }
            })
            .catch((error) => {
                dispatch(
                    getExportStaffFailure(
                        error.response?.data?.message || error.message
                    )
                );
            });
    };
}

export const clearExportStaffState = () => {
    return {
        type: CLEAR_EXPORT_STAFF_STATE
    };
};

export const bulkImportStaff = (formData: FormData, onSuccess: (message: string, failedData: FailedImportData[]) => void, onFailure: (message: string) => void) => {
    return () => {
        return axios.post('/admin/staff/bulk-import', formData, {
            headers: {
                'x-language': 'en',
                'Content-Type': 'multipart/form-data',
            }
        })
        .then((response: AxiosResponse) => {
            const data = response.data;
            if (data.statusCode === 201 || data.statusCode === 200 || response.status === 201) {
                const successCount = data.successCount ?? 0;
                const failedCount = data.failedCount ?? 0;
                onSuccess(`Bulk import completed. Success: ${successCount}, Failed: ${failedCount}`, data.failedData || []);
            } else {
                onFailure(data.message || 'Import failed.');
            }
        })
        .catch((error) => {
            onFailure(error.response?.data?.message || 'Error occurred during bulk import.');
        });
    };
};